import { useState, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useAddons, useUploadAddon, useInstallFromGitHub, useInstallAddon, useDeleteAddon, useApproveAddon, useRejectAddon } from '../../../hooks/useAddons'
import type { GithubAuthMode } from '../../../api/client'
import { useEnterpriseBundle } from '../../../hooks/useEnterprise'
import { useAuthStore } from '../../../store/authStore'
import type { Instance, Addon, SecurityFinding } from '../../../api/client'
import { isBusy } from '../../../lib/instance-status'
import { extractApiError } from '../shared'
import {
  Alert,
  Badge,
  Button,
  Card,
  ConfirmDialog,
  EmptyState,
  LinkButton,
  SegmentedControl,
} from '../../../components/ui'
import { EnterpriseSection } from './EnterpriseSection'
import { GitHubInstallSection } from './GitHubInstall'
import { AddonStatusBadge, SecurityWarning, UploadProgress } from './bits'
import { JobLogsModal, UpdateModal } from './modals'

// ── AddonsTab ─────────────────────────────────────────────────────────────────

export function AddonsTab({ instance }: { instance: Instance }) {
  const { data: addons, isLoading } = useAddons(instance.id)
  const uploadMutation = useUploadAddon(instance.id)
  const githubMutation = useInstallFromGitHub(instance.id)
  const installMutation = useInstallAddon(instance.id)
  const deleteMutation = useDeleteAddon(instance.id)
  const approveMutation = useApproveAddon(instance.id)
  const rejectMutation = useRejectAddon(instance.id)
  const fileRef = useRef<HTMLInputElement>(null)
  const [sourceTab, setSourceTab] = useState<'github' | 'zip' | 'enterprise'>('github')
  const [dragOver, setDragOver] = useState(false)
  const [githubUrl, setGithubUrl] = useState('')
  const [githubRef, setGithubRef] = useState('')
  const [auth, setAuth] = useState<GithubAuthMode>('public')
  const [uploadError, setUploadError] = useState('')
  const [findings, setFindings] = useState<SecurityFinding[]>([])
  const [confirmAddon, setConfirmAddon] = useState<{ id: string; name: string } | null>(null)
  const [deleteError, setDeleteError] = useState('')
  const [logsAddon, setLogsAddon] = useState<Addon | null>(null)
  const [updateAddon, setUpdateAddon] = useState<Addon | null>(null)
  const { t } = useTranslation()
  const rawPlan = useAuthStore((s) => s.plan)
  const role = useAuthStore((s) => s.role)
  const plan = rawPlan ?? 'free'
  const busy = isBusy(instance.status)
  // The Enterprise tab appears only for users an admin has flagged, or when a
  // bundle is already in place (so it stays visible/removable after a revoke).
  // can_upload is computed server-side: the flag lives in the DB, not the JWT.
  const entQ = useEnterpriseBundle(instance.id, plan !== 'free' || role === 'admin')
  const showEnterprise = Boolean(entQ.data?.can_upload || entQ.data?.bundle)
  const sourceTabs: Array<'github' | 'zip' | 'enterprise'> =
    showEnterprise ? ['github', 'zip', 'enterprise'] : ['github', 'zip']

  if (plan === 'free' && role !== 'admin') {
    return (
      <Card padded={false}>
        <EmptyState
          icon={<span className="text-3xl">🧩</span>}
          title={t('detail.addons.planRequired')}
          description={t('detail.addons.planRequiredHint')}
        />
      </Card>
    )
  }

  const handleFile = async (file: File) => {
    setUploadError(''); setFindings([])
    try {
      const res = await uploadMutation.mutateAsync(file)
      if (res.security_findings?.length) setFindings(res.security_findings)
    } catch (e: unknown) {
      const errData = (e as { response?: { data?: { error?: string; security_findings?: SecurityFinding[] } } })?.response?.data
      setUploadError(errData?.error ?? t('detail.addonsExtra.uploadFailed'))
      if (errData?.security_findings?.length) setFindings(errData.security_findings)
    }
  }

  const handleGitHub = async (urlOverride?: string, modules?: string[], refOverride?: string) => {
    setUploadError(''); setFindings([])
    const url = urlOverride ?? githubUrl
    try {
      const res = await githubMutation.mutateAsync({ githubUrl: url, ref: refOverride ?? githubRef, auth, modules })
      if (res.security_findings?.length) setFindings(res.security_findings)
      if (urlOverride === undefined) setGithubUrl('')
    } catch (e: unknown) {
      const errData = (e as { response?: { data?: { code?: string; error?: string; security_findings?: SecurityFinding[] } } })?.response?.data
      // Missing deploy key → flip auth picker to deploy_key so the key panel
      // appears inline. Users don't have to navigate away to resolve this.
      if (errData?.code === 'deploy_key_required') {
        setAuth('deploy_key')
        setUploadError(t('detail.addons.deployKeyNeeded'))
      } else if (errData?.code === 'github_not_connected') {
        setUploadError(t('detail.addons.oauthNotConnected'))
      } else {
        setUploadError(errData?.error ?? t('detail.addonsExtra.githubInstallFailed'))
      }
      if (errData?.security_findings?.length) setFindings(errData.security_findings)
    }
  }

  // mutate + onError, not `await mutateAsync`: nothing here needs the result,
  // and React Query then keeps the rejection in mutation state instead of
  // handing back a promise nobody awaits.
  const handleInstall = (addonId: string) => {
    installMutation.mutate(addonId, {
      onError: (e) => setUploadError(extractApiError(e) ?? t('detail.addonsExtra.installFailed', { defaultValue: 'Install failed' })),
    })
  }

  const handleRetryGitHub = async (addon: Addon) => {
    if (!addon.source_url) return
    setGithubUrl(addon.source_url)
    setSourceTab('github')
    window.scrollTo({ top: 0, behavior: 'smooth' })
    await handleGitHub(addon.source_url)
  }

  const isWorking = (addon: Addon) => addon.status === 'installing' || addon.status === 'updating' || addon.status === 'queued'
  const canInstall = (addon: Addon) => !addon.is_default && (addon.status === 'uploaded' || addon.status === 'failed')
  const canUpdate  = (addon: Addon) => !addon.is_default && (addon.status === 'installed' || addon.status === 'update_failed')
  const isMutating = uploadMutation.isPending || githubMutation.isPending
  const actionLockReason = busy
    ? t('detail.addons.actionLocked', { defaultValue: 'Unavailable while an operation is in progress.' })
    : undefined

  return (
    <div className="space-y-4">
      {/* Source tabs */}
      <Card className="space-y-3">
        <SegmentedControl
          size="sm"
          className="w-fit"
          label={t('detail.addons.sourceLabel', { defaultValue: 'Addon source' })}
          value={sourceTab}
          onChange={setSourceTab}
          options={sourceTabs.map((tab) => ({
            value: tab,
            label:
              tab === 'zip'
                ? t('detail.addons.tabZip')
                : tab === 'enterprise'
                ? <span className="inline-flex items-center gap-1.5"><span aria-hidden className="enterprise-icon">◆</span>{t('detail.addons.tabEnterprise', { defaultValue: 'Enterprise' })}</span>
                : t('detail.addons.tabGithub'),
          }))}
        />

        {sourceTab === 'enterprise' ? (
          <EnterpriseSection instance={instance} />
        ) : sourceTab === 'zip' ? (
          <div
            onDragOver={(e) => { e.preventDefault(); setDragOver(true) }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => {
              e.preventDefault(); setDragOver(false)
              const file = e.dataTransfer.files[0]
              if (file) handleFile(file)
            }}
            onClick={() => fileRef.current?.click()}
            className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
              dragOver ? 'border-brand-400 bg-brand-50' : 'border-gray-300 hover:border-gray-400'
            }`}
          >
            <p className="text-sm font-medium text-gray-700">{t('detail.addons.dropZone')}</p>
            <p className="text-xs text-gray-400 mt-1">{t('detail.addons.dropZoneHint')}</p>
            <input ref={fileRef} type="file" accept=".zip" className="hidden" onChange={(e) => {
              const f = e.target.files?.[0]; if (f) handleFile(f)
            }} />
          </div>
        ) : (
          <GitHubInstallSection
            instanceId={instance.id}
            githubUrl={githubUrl}
            setGithubUrl={setGithubUrl}
            githubRef={githubRef}
            setGithubRef={setGithubRef}
            auth={auth}
            setAuth={setAuth}
            handleGitHub={handleGitHub}
            isMutating={isMutating}
            busy={busy}
            actionLockReason={actionLockReason}
            githubMutation={githubMutation}
            setUploadError={setUploadError}
            t={t}
          />
        )}
      </Card>

      {uploadMutation.isPending && (
        <UploadProgress
          progress={uploadMutation.progress}
          uploadingLabel={t('detail.addons.uploadingPercent', { pct: uploadMutation.progress })}
          processingLabel={t('detail.addons.uploading')}
        />
      )}
      {githubMutation.isPending && <div className="text-sm text-gray-500 text-center">{t('detail.addons.working')}</div>}
      {uploadError && <Alert tone="danger">{uploadError}</Alert>}
      <SecurityWarning findings={findings} quarantined={!!uploadError && findings.some((f) => f.severity === 'critical')} />

      {isLoading ? (
        <div className="text-sm text-gray-400">{t('detail.addons.loading')}</div>
      ) : addons && addons.length > 0 ? (
        <Card padded={false} className="divide-y divide-gray-100">
          {addons.map((addon) => (
            <div key={addon.id} className="group px-6 py-5 flex items-start justify-between gap-5 hover:bg-gray-50/50 transition-colors">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2.5 mb-2 flex-wrap">
                  <h4 className="text-sm font-semibold text-gray-900 truncate" title={addon.name}>
                    {addon.name}
                  </h4>
                  {addon.version && <Badge tone="neutral" className="font-mono">{addon.version}</Badge>}
                  <AddonStatusBadge status={addon.status} />
                  {addon.is_default && <Badge tone="accent">{t('detail.addons.default')}</Badge>}
                </div>

                {addon.summary ? (
                  <p className="text-sm text-gray-600 mb-3 leading-relaxed">{addon.summary}</p>
                ) : (
                  <p className="text-sm text-gray-400 italic mb-3">{t('detail.addonsExtra.noSummary')}</p>
                )}

                <div className="flex items-center gap-4 text-xs text-gray-500 flex-wrap">
                  {addon.author && (
                    <div className="flex items-center gap-1.5" title={t('detail.addonsExtra.author')}>
                      <svg className="w-3.5 h-3.5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                      <span className="text-gray-700 font-medium">{addon.author}</span>
                    </div>
                  )}
                  {addon.license && (
                    <div className="flex items-center gap-1.5" title={t('detail.addonsExtra.license')}>
                      <svg className="w-3.5 h-3.5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span>{addon.license}</span>
                    </div>
                  )}
                  {addon.website && (
                    <a href={addon.website} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 text-brand-600 hover:text-brand-800 hover:underline transition-colors">
                      <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                      </svg>
                      {t('detail.addonsExtra.website')}
                    </a>
                  )}
                  {addon.source_url && (
                    <a href={addon.source_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 text-gray-600 hover:text-gray-900 hover:underline transition-colors">
                      <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z" />
                      </svg>
                      {t('detail.addonsExtra.source')}
                    </a>
                  )}
                </div>
              </div>

              <div className="flex flex-col items-end gap-3 shrink-0 pt-1">
                {addon.status === 'pending_review' && addon.security_findings && addon.security_findings.length > 0 && (
                  <SecurityWarning findings={addon.security_findings} quarantined />
                )}
                {addon.status === 'pending_review' && role === 'admin' && (
                  <div className="flex items-center gap-2">
                    <Button
                      variant="success"
                      size="sm"
                      onClick={() => {
                        setUploadError('')
                        approveMutation.mutate(addon.id, {
                          onError: (e) => setUploadError(extractApiError(e) ?? t('detail.addonsExtra.approveError')),
                        })
                      }}
                      disabled={approveMutation.isPending}
                      loading={approveMutation.isPending && approveMutation.variables === addon.id}
                    >
                      {t('detail.addonsExtra.approve')}
                    </Button>
                    <Button
                      variant="danger"
                      size="sm"
                      onClick={() => {
                        setUploadError('')
                        rejectMutation.mutate(addon.id, {
                          onError: (e) => setUploadError(extractApiError(e) ?? t('detail.addonsExtra.rejectError')),
                        })
                      }}
                      disabled={rejectMutation.isPending}
                      loading={rejectMutation.isPending && rejectMutation.variables === addon.id}
                    >
                      {t('detail.addonsExtra.reject')}
                    </Button>
                  </div>
                )}
                {addon.status === 'pending_review' && role !== 'admin' && (
                  <span className="text-xs text-amber-600 font-medium">{t('detail.addonsExtra.pendingApproval')}</span>
                )}
                {(isWorking(addon) || addon.status === 'installed' || addon.status === 'failed' || addon.status === 'update_failed') && (
                  <LinkButton
                    tone={addon.status === 'failed' || addon.status === 'update_failed' ? 'danger' : 'neutral'}
                    className="inline-flex items-center gap-1"
                    onClick={() => setLogsAddon(addon)}
                  >
                    {(addon.status === 'failed' || addon.status === 'update_failed') && (
                      <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    )}
                    {addon.status === 'failed' || addon.status === 'update_failed' ? t('detail.addonsExtra.viewError') : t('detail.addonsExtra.viewLogs')}
                  </LinkButton>
                )}

                <div className="flex items-center gap-2">
                  {canInstall(addon) && addon.status === 'failed' && addon.source_url && (
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => handleRetryGitHub(addon)}
                      disabled={githubMutation.isPending || busy}
                      title={actionLockReason}
                    >
                      {t('detail.addons.retryGithub')}
                    </Button>
                  )}
                  {canInstall(addon) && (
                    <Button
                      size="sm"
                      onClick={() => handleInstall(addon.id)}
                      disabled={installMutation.isPending || busy}
                      loading={installMutation.isPending && installMutation.variables === addon.id}
                      title={actionLockReason}
                    >
                      {t('detail.addons.install')}
                    </Button>
                  )}
                  {canUpdate(addon) && (
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => setUpdateAddon(addon)}
                      disabled={busy}
                      title={actionLockReason}
                    >
                      {addon.status === 'update_failed'
                        ? t('detail.addons.retryUpdate', { defaultValue: 'Retry Update' })
                        : t('detail.addons.update', { defaultValue: 'Update' })}
                    </Button>
                  )}
                  {!addon.is_default && !isWorking(addon) && (
                    <LinkButton
                      tone="danger"
                      onClick={() => { setDeleteError(''); setConfirmAddon({ id: addon.id, name: addon.name }) }}
                      disabled={deleteMutation.isPending || busy}
                      title={actionLockReason}
                    >
                      {t('detail.addons.remove')}
                    </LinkButton>
                  )}
                </div>
              </div>
            </div>
          ))}
        </Card>
      ) : (
        <EmptyState title={t('detail.addons.empty')} />
      )}

      {confirmAddon && (
        <ConfirmDialog
          title={t('detail.addons.removeTitle')}
          message={
            <>
              <span dangerouslySetInnerHTML={{ __html: t('detail.addons.removeWarning', { name: `<strong>${confirmAddon.name}</strong>` }) }} />
              {deleteError && <span className="mt-2 block text-red-600">{deleteError}</span>}
            </>
          }
          confirmLabel={t('detail.addons.removeConfirm')}
          cancelLabel={t('detail.addons.removeCancel')}
          busy={deleteMutation.isPending}
          onConfirm={() => {
            setDeleteError('')
            deleteMutation.mutate(confirmAddon.id, {
              onSuccess: () => setConfirmAddon(null),
              onError: (e) => setDeleteError(extractApiError(e) ?? t('detail.addons.removeError')),
            })
          }}
          onCancel={() => { setConfirmAddon(null); setDeleteError('') }}
        />
      )}

      {logsAddon && (
        <JobLogsModal instanceId={instance.id} addon={logsAddon} onClose={() => setLogsAddon(null)} />
      )}
      {updateAddon && (
        <UpdateModal instanceId={instance.id} addon={updateAddon} onClose={() => { setUpdateAddon(null) }} />
      )}
    </div>
  )
}
